/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";

import { Box, Fade, IconButton, Modal, Stack, Step, StepLabel, Stepper, Typography, useTheme } from "@mui/material";

import Backdrop from "@mui/material/Backdrop";
import {
    Effect,
    InventoryItem,
    InventoryItemWithAvailable,
    ModalBase,
    Nullable,
    StorageLocation,
    TechnicianEntryEgressInventory,
} from "../../utils/Interfaces";

import { Close } from "@mui/icons-material";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { getMyInventories } from "../tech-work-order/technicianWorkOrderFunctions";
import { delayedAction, getInventoriesInStorageLocation, listStorageLocationsFromInventories } from "../../utils/Functions";
import { useWindowDimensions } from "../../utils/Hooks";
import InventorySelector from "../../components/modal/transfers/InventorySelector";
import AmountSelectorWithNotes from "../../components/modal/transfers/AmountSelectorWithNotes";
import StorageLocationSelector from "../../components/modal/transfers/StorageLocationSelector";
import { useAllStorageLocations } from "../materials-storage-location/storageLocationHooks";

export interface InventoryEgressModalProps extends ModalBase {
    onMaterialTransferRequested: Effect;
    existingTechnicianEntryEgresses: TechnicianEntryEgressInventory[];
    onSubmitMaterialTransferRequest: (
        fromInventoryId: number,
        toInventoryId: number,
        materialId: number,
        amount: number,
        notes: Nullable<string>,
        modalCallback: Effect
    ) => void;
}

function InventoryTechTransferModal(props: InventoryEgressModalProps) {
    const { open, handleClose, onMaterialTransferRequested, existingTechnicianEntryEgresses, onSubmitMaterialTransferRequest } =
        props;

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const { width } = useWindowDimensions();
    const mobile = width <= 600;

    const [activeStep, setActiveStep] = useState<number>(0);

    const [selectedFromStorageLocation, setSelectedFromStorageLocation] = useState<Nullable<StorageLocation>>(null);
    const [selectedToStorageLocation, setSelectedToStorageLocation] = useState<Nullable<StorageLocation>>(null);
    const [selectedInventory, setSelectedInventory] = useState<Nullable<InventoryItem>>(null);
    const [amount, setAmount] = useState<number>(0);
    const [notes, setNotes] = useState<string>("");
    const [submitting, setSubmitting] = useState<boolean>(false);

    const [inventories, setInventories] = useState<InventoryItem[]>([]);

    function onClose() {
        handleClose();
        void delayedAction(500, () => {
            setSelectedInventory(null);
            setSelectedFromStorageLocation(null);
            setAmount(0);
            setActiveStep(0);
            setSubmitting(false);
            setNotes("");
        });
    }

    const { data } = useAllStorageLocations();

    useEffect(() => {
        getMyInventories(dispatch, setInventories);
    }, [open]);

    const theme = useTheme();

    return (
        <Box>
            <Modal
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                open={open}
                onClose={onClose}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{
                    timeout: 500,
                }}
            >
                <Fade in={open}>
                    <Box
                        sx={{
                            backgroundColor: theme.palette.background.paper,
                            border: "1px solid #000",
                            boxShadow: theme.shadows[5],
                            padding: theme.spacing(2, 4, 3),
                            zIndex: 1000,
                            minWidth: mobile ? "100vw" : "90vw",
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between">
                            <Typography variant="h5" sx={{ mb: 3 }}>
                                Initiate Material Transfer
                            </Typography>
                            <Stack direction="column" justifyContent="flex-start">
                                <IconButton sx={{ mt: -1, mr: -3 }} onClick={onClose}>
                                    <Close fontSize="large" />
                                </IconButton>
                            </Stack>
                        </Stack>

                        <Stepper activeStep={activeStep}>
                            <Step>
                                <StepLabel>From</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel>Material</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel>To</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel>Amount</StepLabel>
                            </Step>
                        </Stepper>

                        {activeStep === 0 && (
                            <StorageLocationSelector
                                storageLocations={listStorageLocationsFromInventories(inventories)}
                                setSelectedStorageLocation={setSelectedFromStorageLocation}
                                progressDialog={() => setActiveStep(1)}
                            />
                        )}

                        {activeStep === 1 && (
                            <InventorySelector
                                inventories={getInventoriesInStorageLocation(
                                    selectedFromStorageLocation,
                                    existingTechnicianEntryEgresses,
                                    inventories
                                )}
                                setSelectedInventory={setSelectedInventory}
                                progressDialog={() => setActiveStep(2)}
                                regressDialog={() => {
                                    setActiveStep(0);
                                    setSelectedFromStorageLocation(null);
                                }}
                            />
                        )}

                        {activeStep === 2 && (
                            <StorageLocationSelector
                                storageLocations={(data?.results ?? []).filter(
                                    (storageLocation: StorageLocation) =>
                                        storageLocation.location_type.toLowerCase().includes("van") &&
                                        (storageLocation.id !== selectedFromStorageLocation?.id ?? -1)
                                )}
                                setSelectedStorageLocation={setSelectedToStorageLocation}
                                progressDialog={() => setActiveStep(3)}
                                regressDialog={() => setActiveStep(1)}
                            />
                        )}

                        {activeStep === 3 && (
                            <AmountSelectorWithNotes
                                inventory={selectedInventory as InventoryItemWithAvailable}
                                submitLabel="Initiate Transfer"
                                amount={amount}
                                setAmount={setAmount}
                                notes={notes}
                                setNotes={setNotes}
                                submitting={submitting}
                                progressDialog={() => {
                                    setSubmitting(true);

                                    onSubmitMaterialTransferRequest(
                                        selectedFromStorageLocation?.id ?? -1,
                                        selectedToStorageLocation?.id ?? -1,
                                        selectedInventory?.material.id ?? -1,
                                        amount,
                                        notes,
                                        () => {
                                            onMaterialTransferRequested();
                                            onClose();
                                        }
                                    );
                                }}
                                regressDialog={() => {
                                    setActiveStep(2);
                                    setSelectedInventory(null);
                                }}
                            />
                        )}
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
}

export default InventoryTechTransferModal;
